/**
 * WidgetPage — Same-origin DOM portal approach.
 *
 * Supports two display modes controlled via the `display` URL parameter
 * or the `widgetCardConfig.display` prop:
 *
 *   • `card` (default) — Shows a clickable thumbnail card. Clicking opens
 *     the learning-module modal (portalled to the parent page when
 *     same-origin, or fullscreen-fallback for cross-origin iframes).
 *
 *   • `inline` — Embeds the content directly in the iframe. For video
 *     modules, renders the video player (like a YouTube embed). For PDF
 *     modules, renders a native PDF viewer. Supports browser fullscreen.
 *     Intended for clients that want a simple inline content experience.
 *
 * No parent-side JavaScript required. Falls back to inline fullscreen
 * rendering for cross-origin iframes.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { WidgetCard } from '@amzn/seller-university-ui-components';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import {
  isInIframe,
  isSameOriginIframe,
  hasParentPortalContainer,
  getParentPortalContainer,
  removeParentPortalContainer,
  expandIframeToFullscreen,
  restoreIframeSize,
} from 'src/utils/widgetParentPortal';
import { ParentEntityProvider } from 'src/contexts/ParentEntityContext';
import LearningModuleModal from '../../components/learning-module/LearningModuleModal';
import LoadingView from '../../components/LoadingView';
import ErrorView from '../../components/ErrorView';
import { getStatusCodeFromError } from '../../utils/error';
import useWidgetPageData, { UseWidgetPageDataProps } from './useWidgetPageData';
import VideoPlayer from '../../components/learning-module/video/VideoPlayer';
import { WidgetCardConfig } from './types';

export type { WidgetCardConfig };

export interface WidgetPageProps extends UseWidgetPageDataProps {}

const WidgetPage = (props: WidgetPageProps) => {
  const {
    learningModuleId,
    location,
    moduleData,
    isLoading,
    isError,
    error,
    finalCardConfig,
    finalDisplay,
    contentUrl,
    defaultInstance,
    finalClientId,
    finalUnauthenticated,
    finalParentId,
    finalParentType,
    refTag,
    referral,
    emitClickMetric,
  } = useWidgetPageData(props);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const embeddedInIframe = isInIframe();
  const canPortalToParent = isSameOriginIframe();
  // Same-origin but parent lacks Katal — expand iframe instead of portalling.
  // Cached in a ref so the 3 cross-frame DOM reads (window.top.document,
  // customElements.get, getElementById) only run once on mount.
  const canExpandIframeRef = useRef(canPortalToParent && !hasParentPortalContainer());
  const canExpandIframe = canExpandIframeRef.current;

  const handleModalOpen = () => {
    if (canExpandIframe) expandIframeToFullscreen();
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (canExpandIframe) {
      restoreIframeSize();
    }
    removeParentPortalContainer();
  };

  // Clean up portal container and iframe size on unmount.
  // Uses hasParentPortalContainer() (read-only) instead of
  // getParentPortalContainer() to avoid creating DOM elements as a
  // side effect during cleanup.
  useEffect(() => {
    return () => {
      if (isSameOriginIframe() && !hasParentPortalContainer()) {
        restoreIframeSize();
      }
      removeParentPortalContainer();
    };
  }, []);

  if (isLoading) return <LoadingView />;

  if (isError) {
    const statusCode = getStatusCodeFromError(error);
    const isClientError = statusCode !== undefined && statusCode >= 400 && statusCode < 500;

    const logContext = {
      errorType: isClientError
        ? 'Resource not found or access restricted for widget module'
        : 'Failed to load widget module',
      learningModuleId,
      statusCode,
      refTag,
      referral,
      path: location.pathname,
      url: window.location.href,
      parentType: finalParentType,
      parentId: finalParentId,
      clientId: finalClientId,
    };

    if (isClientError) {
      // 4xx errors (e.g., 403 unauthenticated) are expected client-side
      // scenarios — API errors already classified in baseQuery.ts
      ClientLogger.warn(
        ClientLog.IllegalInternalDataState,
        error instanceof Error ? error : new Error(String(error)),
        logContext,
      );
    } else {
      ClientLogger.error(
        ClientLog.IllegalInternalDataState,
        error instanceof Error ? error : new Error(String(error)),
        logContext,
      );
    }

    return <ErrorView />;
  }

  if (!moduleData) {
    const moduleError = new Error('Learning module not found or empty response');
    ClientLogger.error(ClientLog.IllegalInternalDataState, moduleError, {
      learningModuleId,
      refTag,
      referral,
      path: location.pathname,
      url: window.location.href,
      parentType: finalParentType,
      parentId: finalParentId,
      clientId: finalClientId,
    });
    return <ErrorView />;
  }

  const renderModal = () => {
    if (!isModalOpen) return null;

    const modalContent = (
      <LearningModuleModal
        learningModuleId={learningModuleId}
        parentId={finalParentId}
        parentType={finalParentType}
        onModalClose={handleModalClose}
        unauthenticated={finalUnauthenticated}
        clientId={finalClientId}
        isPortalRendered={canPortalToParent}
      />
    );

    if (canPortalToParent) {
      const portalContainer = getParentPortalContainer();
      if (portalContainer) {
        return createPortal(modalContent, portalContainer);
      }
    }

    return modalContent;
  };

  // Validate content prerequisites before rendering any display mode
  if (!contentUrl || !defaultInstance || !defaultInstance.instanceType) {
    ClientLogger.error(
      ClientLog.IllegalInternalDataState,
      new Error('No content URL or instance type available for widget rendering'),
      {
        learningModuleId,
        refTag,
        referral,
        path: location.pathname,
        url: window.location.href,
        hasContentUrl: !!contentUrl,
        hasDefaultInstance: !!defaultInstance,
        instanceType: defaultInstance?.instanceType,
      },
    );
    return <ErrorView />;
  }

  // --- Inline display mode (video or PDF) ---
  if (finalDisplay === 'inline') {
    const instanceType = defaultInstance.instanceType.toUpperCase();
    switch (instanceType) {
      case 'VIDEO':
        return (
          <ParentEntityProvider parentId={finalParentId} parentType={finalParentType}>
            <div data-testid="inline-video-container">
              <VideoPlayer
                videoUrl={contentUrl}
                learningModuleInstance={defaultInstance}
                clientId={finalClientId}
              />
            </div>
          </ParentEntityProvider>
        );
      case 'DOCUMENT':
        return (
          <ParentEntityProvider parentId={finalParentId} parentType={finalParentType}>
            <div
              className="h-screen w-full bg-su-background-container-content"
              data-testid="inline-pdf-container"
            >
              <object
                className="size-full"
                type="application/pdf"
                data={contentUrl}
                aria-label={moduleData?.title || 'Document'}
              />
            </div>
          </ParentEntityProvider>
        );
      // Unsupported types fall through to card mode below
      // istanbul ignore next: unreachable — resolveContentUrl only provides URLs for VIDEO/DOCUMENT
      default:
        break;
    }
  }

  // --- Card display mode (default) ---

  // Cross-origin iframe fallback: fullscreen the iframe content
  const needsFullscreenFallback = embeddedInIframe && isModalOpen && !canPortalToParent;

  return (
    <div className={needsFullscreenFallback ? 'inset-0 bg-white fixed z-[9999]' : ''}>
      {finalCardConfig && !isModalOpen && (
        <WidgetCard
          id={finalCardConfig.id}
          title={finalCardConfig.title}
          description={finalCardConfig.description}
          imageSrc={finalCardConfig.imageSrc}
          contentVariant={finalCardConfig.contentVariant}
          metadata={finalCardConfig.metadata}
          className={finalCardConfig.className}
          onClick={() => {
            handleModalOpen();
            emitClickMetric();
          }}
        />
      )}

      {renderModal()}
    </div>
  );
};

export default WidgetPage;
